import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { CreditCard, Plus, Pencil, Trash2, Loader2, X, Check, AlertCircle, Power } from "lucide-react";
import {
  getAdminPlans, createAdminPlan, updateAdminPlan, toggleAdminPlan, deleteAdminPlan,
  AdminPlan,
} from "../../api/services/adminPlanService";
import { getAdminTaxProfiles, AdminTaxProfile } from "../../api/services/adminTaxProfileService";
import { getErrorMessage } from "@/utils/errorUtils";
import { formatPrice } from "@/utils/formatPrice";
import { toast } from "sonner";

const PERIODS: AdminPlan["period"][] = ["day", "week", "month", "year"];

const emptyPlan = (): Partial<AdminPlan> => ({
  name: "", price: 0, period: "month", features: [], dataLimit: null,
  dataLimitUnit: "GB", hasUnlimitedAccess: true, isActive: true, displayOrder: 0, description: "",
  price_minor: undefined, currency: "USD", country_code: "", duration_days: undefined,
  device_limit: undefined, data_cap_mb: null, fair_use_mb_day: null, rate_down_kbps: null,
  rate_up_kbps: null, rate_after_fup_kbps: null, tax_profile_id: "", effective_from: "", effective_to: ""
});

export default function AdminPlans() {
  const { t } = useTranslation();
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<Partial<AdminPlan> | null>(null);
  const [featuresText, setFeaturesText] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<AdminPlan | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showT04, setShowT04] = useState(false);
  const [taxProfiles, setTaxProfiles] = useState<AdminTaxProfile[]>([]);

  const load = async () => {
    setLoading(true); setErr("");
    try { 
      const [p, tp] = await Promise.all([getAdminPlans(), getAdminTaxProfiles().catch(() => [])]);
      setPlans(p); 
      setTaxProfiles(tp);
    }
    catch (e: unknown) { setErr(getErrorMessage(e) || "Failed to load plans"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(emptyPlan()); setFeaturesText(""); setModalOpen(true); };
  const openEdit = (p: AdminPlan) => {
    // The backend may send the id as `id`; make sure `_id` is set so save() uses
    // PUT (update) rather than POST (create), and the modal shows "Edit Plan".
    setEditing({ ...p, _id: p._id ?? p.id });
    setFeaturesText((p.features || []).join("\n"));
    setModalOpen(true);
  };
  const closeModal = () => { setModalOpen(false); setEditing(null); };

  const save = async () => {
    if (!editing) return;
    if (!editing.name || editing.price === undefined || editing.price === null || !editing.period) {
      setErr("Name, price and period are required"); return;
    }
    setSaving(true); setErr("");
    const payload: Partial<AdminPlan> = {
      ...editing,
      price: Number(editing.price),
      displayOrder: Number(editing.displayOrder || 0),
      dataLimit: editing.dataLimit === null || editing.dataLimit === undefined || (editing.dataLimit as string | number) === ""
        ? null : Number(editing.dataLimit),
      features: featuresText.split("\n").map(s => s.trim()).filter(Boolean),
      price_minor: editing.price_minor ? Number(editing.price_minor) : undefined,
      duration_days: editing.duration_days ? Number(editing.duration_days) : undefined,
      device_limit: editing.device_limit ? Number(editing.device_limit) : undefined,
      data_cap_mb: editing.data_cap_mb ? Number(editing.data_cap_mb) : null,
      fair_use_mb_day: editing.fair_use_mb_day ? Number(editing.fair_use_mb_day) : null,
      rate_down_kbps: editing.rate_down_kbps ? Number(editing.rate_down_kbps) : null,
      rate_up_kbps: editing.rate_up_kbps ? Number(editing.rate_up_kbps) : null,
      rate_after_fup_kbps: editing.rate_after_fup_kbps ? Number(editing.rate_after_fup_kbps) : null,
      tax_profile_id: editing.tax_profile_id || null,
      effective_from: editing.effective_from || null,
      effective_to: editing.effective_to || null,
    };
    try {
      // Resolve the id from either field: an existing plan => PUT /admin/plans/:id,
      // a new plan (no id) => POST /admin/plans.
      const planId = editing._id ?? editing.id;
      if (planId) {
        await updateAdminPlan(planId, payload);
        toast.success("Plan updated");
      } else {
        await createAdminPlan(payload);
        toast.success("Plan created");
      }
      closeModal(); await load();
    } catch (e: unknown) {
      console.error("Save plan failed:", e);
      const msg = getErrorMessage(e) || "Failed to save plan";
      setErr(msg); toast.error(msg);
    }
    finally { setSaving(false); }
  };

  const onToggle = async (p: AdminPlan) => {
    if (!p._id) {
      const msg = "This plan has no id — cannot toggle. Reload the page and try again.";
      setErr(msg); toast.error(msg);
      return;
    }
    setBusyId(p._id);
    setErr("");
    try {
      // PATCH /admin/plans/:id/toggle (empty body) — backend flips isActive.
      await toggleAdminPlan(p._id);
      toast.success(p.isActive ? "Plan deactivated" : "Plan activated");
      await load();
    }
    catch (e: unknown) {
      console.error("Toggle plan failed:", e);
      const msg = getErrorMessage(e) || "Failed to toggle plan";
      setErr(msg); toast.error(msg);
    }
    finally { setBusyId(null); }
  };

  const doDelete = async () => {
    if (!confirmDelete?._id) {
      const msg = "This plan has no id — cannot delete. Reload the page and try again.";
      setErr(msg); toast.error(msg);
      return;
    }
    setBusyId(confirmDelete._id);
    setErr("");
    try {
      const ok = await deleteAdminPlan(confirmDelete._id);
      if (!ok) { const msg = "Failed to delete plan"; setErr(msg); toast.error(msg); return; }
      toast.success("Plan deleted");
      setConfirmDelete(null);
      await load();
    }
    catch (e: unknown) {
      console.error("Delete plan failed:", e);
      const msg = getErrorMessage(e) || "Failed to delete plan";
      setErr(msg); toast.error(msg);
    }
    finally { setBusyId(null); }
  };

  const fld = "w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm";

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><CreditCard className="w-6 h-6" /> {t("admin.plans.title", { defaultValue: "Subscription Plans" })}</h1>
          <p className="text-sm text-gray-500 mt-1">{t("admin.plans.subtitle", { defaultValue: "Create, edit, activate or remove the plans users can subscribe to." })}</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-medium">
          <Plus className="w-4 h-4" /> {t("admin.plans.actions.addPlan", { defaultValue: "Add Plan" })}
        </button>
      </div>

      {err && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg px-3 py-2">
          <AlertCircle className="w-4 h-4" /> {err}
        </div>
      )}

      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> {t("admin.plans.loading", { defaultValue: "Loading plans…" })}</div>
        ) : plans.length === 0 ? (
          <div className="text-center py-16 text-gray-500">{t("admin.plans.noPlans", "No plans yet. Click ")}<span className="font-medium">Add Plan</span>{t("admin.plans.toCreate", " to create one.")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-gray-500 border-b border-gray-200 dark:border-gray-800">
                <tr>
                  <th className="px-4 py-3 font-medium">{t("admin.plans.table.name", "Name")}</th>
                  <th className="px-4 py-3 font-medium">{t("admin.plans.table.price", "Price")}</th>
                  <th className="px-4 py-3 font-medium">{t("admin.plans.table.country", "Country")}</th>
                  <th className="px-4 py-3 font-medium">{t("admin.plans.table.period", "Period")}</th>
                  <th className="px-4 py-3 font-medium">{t("admin.plans.table.data", "Data")}</th>
                  <th className="px-4 py-3 font-medium">{t("admin.plans.table.status", "Status")}</th>
                  <th className="px-4 py-3 font-medium text-right">{t("admin.plans.table.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((p) => (
                  <tr key={p._id} className="border-b border-gray-100 dark:border-gray-800/60">
                    <td className="px-4 py-3">
                      <div className="font-medium">{p.name}</div>
                      {p.description ? <div className="text-xs text-gray-400 mt-0.5">{p.description}</div> : null}
                    </td>
                    <td className="px-4 py-3 font-medium">
                      {p.price_minor !== undefined && p.currency ? formatPrice(p.price_minor, p.currency) : `$${p.price}`}
                    </td>
                    <td className="px-4 py-3">
                      {p.country_code ? <span className="uppercase font-medium text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">{p.country_code}</span> : <span className="text-gray-400 text-xs">Global</span>}
                    </td>
                    <td className="px-4 py-3 capitalize">
                      {p.period} {p.duration_days ? <span className="text-xs text-gray-400">({p.duration_days}d)</span> : ""}
                    </td>
                    <td className="px-4 py-3">{p.hasUnlimitedAccess && (p.dataLimit == null) ? "Unlimited" : `${p.dataLimit} ${p.dataLimitUnit || "GB"}`}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${p.isActive ? "bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-400" : "bg-gray-200 text-gray-600 dark:bg-gray-800 dark:text-gray-400"}`}>
                        {p.isActive ? t("admin.plans.status.active", "Active") : t("admin.plans.status.inactive", "Inactive")}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => onToggle(p)} disabled={busyId === p._id} title={p.isActive ? t("admin.plans.actions.deactivate", "Deactivate") : t("admin.plans.actions.activate", "Activate")} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50">
                          {busyId === p._id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className={`w-4 h-4 ${p.isActive ? "text-green-600" : "text-gray-400"}`} />}
                        </button>
                        <button onClick={() => openEdit(p)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => setConfirmDelete(p)} className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {modalOpen && editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeModal}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800">
              <h2 className="text-lg font-semibold">{editing._id ? t("admin.plans.modal.edit", "Edit Plan") : t("admin.plans.modal.new", "New Plan")}</h2>
              <button onClick={closeModal} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t("admin.plans.form.name", { defaultValue: "Name" })}</label>
                <input className={fld} value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. Daily Unlimited" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{t("admin.plans.form.price", { defaultValue: "Price" })}</label>
                  <input type="number" className={fld} value={editing.price ?? 0} onChange={(e) => setEditing({ ...editing, price: e.target.value as unknown as number })} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t("admin.plans.form.period", { defaultValue: "Period" })}</label>
                  <select className={fld} value={editing.period} onChange={(e) => setEditing({ ...editing, period: e.target.value as AdminPlan["period"] })}>
                    {PERIODS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{t("admin.plans.form.dataLimit", { defaultValue: "Data limit (blank = unlimited)" })}</label>
                  <input type="number" className={fld} value={editing.dataLimit ?? ""} onChange={(e) => setEditing({ ...editing, dataLimit: e.target.value as unknown as number })} placeholder="Unlimited" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t("admin.plans.form.dataUnit", { defaultValue: "Data unit" })}</label>
                  <input className={fld} value={editing.dataLimitUnit || "GB"} onChange={(e) => setEditing({ ...editing, dataLimitUnit: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t("admin.plans.form.description", { defaultValue: "Description" })}</label>
                <input className={fld} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t("admin.plans.form.features", { defaultValue: "Features (one per line)" })}</label>
                <textarea className={`${fld} min-h-[90px]`} value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} placeholder={"Unlimited browsing\nHD streaming\nPriority support"} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{t("admin.plans.form.displayOrder", { defaultValue: "Display order" })}</label>
                  <input type="number" className={fld} value={editing.displayOrder ?? 0} onChange={(e) => setEditing({ ...editing, displayOrder: e.target.value as unknown as number })} />
                </div>
                <label className="flex items-center gap-2 mt-7 text-sm">
                  <input type="checkbox" checked={!!editing.isActive} onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })} />
                  {t("admin.plans.form.active", { defaultValue: "Active" })}
                </label>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editing.hasUnlimitedAccess} onChange={(e) => setEditing({ ...editing, hasUnlimitedAccess: e.target.checked })} />
                {t("admin.plans.form.unlimitedAccess", { defaultValue: "Unlimited access" })}
              </label>

              {/* T-04 section toggle */}
              <div className="pt-2 border-t border-gray-200 dark:border-gray-800">
                <button type="button" onClick={() => setShowT04(!showT04)} className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1">
                  {showT04 ? t("admin.plans.form.hide", { defaultValue: "Hide" }) : t("admin.plans.form.show", { defaultValue: "Show" })} {t("admin.plans.form.advancedT04", { defaultValue: "Advanced Multi-Jurisdiction Pricing (T-04)" })}
                </button>
              </div>

              {showT04 && (
                <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg space-y-4 border border-gray-200 dark:border-gray-800">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">{t("admin.plans.form.currency", { defaultValue: "Currency" })}</label>
                      <select className={fld} value={editing.currency || "USD"} onChange={(e) => setEditing({ ...editing, currency: e.target.value })}>
                        <option value="USD">USD ($)</option>
                        <option value="NGN">NGN (₦)</option>
                        <option value="EUR">EUR (€)</option>
                        <option value="GBP">GBP (£)</option>
                        <option value="XAF">XAF (FCFA)</option>
                        <option value="XOF">XOF (CFA)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">{t("admin.plans.form.priceMinor", { defaultValue: "Price (minor units e.g. cents/kobo)" })}</label>
                      <input type="number" className={fld} value={editing.price_minor ?? ""} onChange={(e) => setEditing({ ...editing, price_minor: e.target.value ? Number(e.target.value) : undefined })} placeholder="e.g. 50000 for 500.00" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">{t("admin.plans.form.countryCode", { defaultValue: "Country Code (ISO 2-letter)" })}</label>
                      <input className={fld} value={editing.country_code || ""} onChange={(e) => setEditing({ ...editing, country_code: e.target.value.toUpperCase() })} placeholder="e.g. NG, US" maxLength={2} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">{t("admin.plans.form.durationDays", { defaultValue: "Duration (days)" })}</label>
                      <input type="number" className={fld} value={editing.duration_days ?? ""} onChange={(e) => setEditing({ ...editing, duration_days: e.target.value ? Number(e.target.value) : undefined })} placeholder="e.g. 30" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">{t("admin.plans.form.taxProfile", { defaultValue: "Tax Profile" })}</label>
                      <select className={fld} value={editing.tax_profile_id || ""} onChange={(e) => setEditing({ ...editing, tax_profile_id: e.target.value })}>
                        <option value="">{t("admin.plans.form.none", { defaultValue: "None" })}</option>
                        {taxProfiles.map(tp => (
                          <option key={tp._id} value={tp._id}>{tp.label} ({tp.country_code})</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">{t("admin.plans.form.deviceLimit", { defaultValue: "Device Limit" })}</label>
                      <input type="number" className={fld} value={editing.device_limit ?? ""} onChange={(e) => setEditing({ ...editing, device_limit: e.target.value ? Number(e.target.value) : undefined })} placeholder="e.g. 5" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">{t("admin.plans.form.effectiveFrom", { defaultValue: "Effective From" })}</label>
                      <input type="datetime-local" className={fld} value={editing.effective_from ? new Date(editing.effective_from).toISOString().slice(0, 16) : ""} onChange={(e) => setEditing({ ...editing, effective_from: e.target.value ? new Date(e.target.value).toISOString() : "" })} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">{t("admin.plans.form.effectiveTo", { defaultValue: "Effective To" })}</label>
                      <input type="datetime-local" className={fld} value={editing.effective_to ? new Date(editing.effective_to).toISOString().slice(0, 16) : ""} onChange={(e) => setEditing({ ...editing, effective_to: e.target.value ? new Date(e.target.value).toISOString() : "" })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">{t("admin.plans.form.speedDown", { defaultValue: "Speed Down (kbps)" })}</label>
                      <input type="number" className={fld} value={editing.rate_down_kbps ?? ""} onChange={(e) => setEditing({ ...editing, rate_down_kbps: e.target.value ? Number(e.target.value) : undefined })} placeholder="Unlimited" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">{t("admin.plans.form.speedUp", { defaultValue: "Speed Up (kbps)" })}</label>
                      <input type="number" className={fld} value={editing.rate_up_kbps ?? ""} onChange={(e) => setEditing({ ...editing, rate_up_kbps: e.target.value ? Number(e.target.value) : undefined })} placeholder="Unlimited" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">{t("admin.plans.form.afterFUP", { defaultValue: "After FUP (kbps)" })}</label>
                      <input type="number" className={fld} value={editing.rate_after_fup_kbps ?? ""} onChange={(e) => setEditing({ ...editing, rate_after_fup_kbps: e.target.value ? Number(e.target.value) : undefined })} placeholder="Unlimited" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium mb-1">{t("admin.plans.form.dataCap", { defaultValue: "Data Cap (MB)" })}</label>
                      <input type="number" className={fld} value={editing.data_cap_mb ?? ""} onChange={(e) => setEditing({ ...editing, data_cap_mb: e.target.value ? Number(e.target.value) : undefined })} placeholder="Unlimited" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">{t("admin.plans.form.fairUse", { defaultValue: "Fair Use per Day (MB)" })}</label>
                      <input type="number" className={fld} value={editing.fair_use_mb_day ?? ""} onChange={(e) => setEditing({ ...editing, fair_use_mb_day: e.target.value ? Number(e.target.value) : undefined })} placeholder="Unlimited" />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-200 dark:border-gray-800">
              <button onClick={closeModal} className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700">{t("admin.plans.modal.cancel", "Cancel")}</button>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {t("admin.plans.actions.save", { defaultValue: "Save" })}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setConfirmDelete(null)}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-sm shadow-xl p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-2">{t("admin.plans.modal.deleteTitle", "Delete plan?")}</h3>
            <p className="text-sm text-gray-500 mb-5">{t("admin.plans.modal.deleteWarning1", { defaultValue: "This permanently removes " })}<span className="font-medium">{confirmDelete.name}</span>{t("admin.plans.modal.deleteWarning2", { defaultValue: ". Existing subscriptions are not affected, but users can no longer pick it." })}</p>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700">{t("admin.plans.modal.cancel", "Cancel")}</button>
              <button onClick={doDelete} disabled={busyId === confirmDelete._id} className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-50">
                {busyId === confirmDelete._id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} {t("admin.plans.actions.delete", { defaultValue: "Delete" })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
